import { useEffect, useRef, useState } from 'react';

/**
 * messaging#24 - the signed-in person's unread messages, for the red count on the "Messages" tab,
 * the Dashboard's "Unread Messages" tile and the "new message" alert.
 *
 * Read from Messaging (GET https://messages.credifyfast.com/api/unread, with the shared sign-in
 * cookie): the same per-room counts the Messaging app shows, which clear when the person reads
 * them there. Only a number comes back: no names and no message text reach the EHR.
 *
 * messaging#59 (Chase 10-18: "It takes 11 seconds for the dot to show up") - one shared
 * long-poll serves every subscriber: each request says what we last saw (`known`) and Messaging
 * holds it (up to 25 s) until the count changes, so a new message shows within a second or two.
 * Each answer is followed by the next request straight away. While the tab is hidden nothing is
 * asked; coming back reads at once (and alerts, as before). Messaging unreachable: try again in
 * 5 s, then every 30 s. An older Messaging that answers at once: every 15 s, as before.
 *
 * messaging#15 (Jay, 2026-10-07) - the admin "View as" changes only what the EHR shows; Messaging
 * always sends as the person signed in. The Messages link tells it who is being viewed, so it can
 * say so.
 */
export const MESSAGES_URL = 'https://messages.credifyfast.com/';
const UNREAD_URL = 'https://messages.credifyfast.com/api/unread';
const WAIT_S = 25; // messaging#59: Messaging holds the request at most this long
const FALLBACK_MS = 15_000; // an older Messaging (no long-poll): read every 15 s
const RETRY_FIRST_MS = 5_000; // Messaging unreachable: try again in 5 s,
const RETRY_LATER_MS = 30_000; // then every 30 s
const EARLY_MS = 2_000; // an unchanged answer sooner than this = Messaging didn't hold it

export function messagesUrl(viewingAs?: string | null): string {
  const v = (viewingAs ?? '').trim();
  return v ? `${MESSAGES_URL}?viewingAs=${encodeURIComponent(v)}` : MESSAGES_URL;
}

export interface Unread {
  /** null until the first answer, or when Messaging can't be reached. */
  count: number | null;
  /** Time of the newest unread message, ms. */
  latestTs: number | null;
}

/** An answer, with messaging#59's signature of it (`known` on the next request). */
export interface UnreadAnswer extends Unread {
  sig: string | null;
}

/** A new message arrived between two reads: more unread, or a newer unread one. */
export function arrived(prev: Unread, next: Unread): boolean {
  if (prev.count == null || next.count == null || next.count === 0) return false;
  return next.count > prev.count || (next.latestTs ?? 0) > (prev.latestTs ?? 0);
}

export async function fetchUnread(
  fetchImpl: typeof fetch = fetch,
  opts: { wait?: number; known?: string | null; signal?: AbortSignal } = {},
): Promise<UnreadAnswer> {
  const none = { count: null, latestTs: null, sig: null };
  let url = UNREAD_URL;
  if (opts.wait) {
    url += `?wait=${opts.wait}`;
    if (opts.known != null) url += `&known=${encodeURIComponent(opts.known)}`;
  }
  try {
    const r = await fetchImpl(url, { credentials: 'include', signal: opts.signal });
    if (!r.ok) return none;
    const j = (await r.json()) as { total?: unknown; rooms?: unknown; latestTs?: unknown };
    const count = typeof j.total === 'number' && j.total >= 0 ? j.total : null;
    const latestTs = typeof j.latestTs === 'number' ? j.latestTs : null;
    const rooms = typeof j.rooms === 'number' ? j.rooms : 0;
    // the same string Messaging builds (src/routes/unread.js sigOf)
    return { count, latestTs, sig: count == null ? null : `${count}.${rooms}.${latestTs ?? 0}` };
  } catch {
    return none;
  }
}

/**
 * messaging#59: after an answer, show it or not, and how long to wait before asking again
 * (0 = at once). `fails` = errors in a row before this one.
 */
export function afterAnswer(
  next: UnreadAnswer,
  known: string | null,
  tookMs: number,
  fails: number,
): { show: boolean; waitMs: number; fails: number } {
  if (next.count == null) {
    return {
      show: false,
      waitMs: fails ? RETRY_LATER_MS : RETRY_FIRST_MS,
      fails: fails + 1,
    };
  }
  const same = next.sig === known;
  return { show: !same, waitMs: same && tookMs < EARLY_MS ? FALLBACK_MS : 0, fails: 0 };
}

/* One shared long-poll for all subscribers. */
let current: Unread = { count: null, latestTs: null };
let currentSig: string | null = null;
const listeners = new Set<(u: Unread, prev: Unread) => void>();
let started = false;
let running = false;
let fails = 0;
let ctl: AbortController | undefined;
let wake: (() => void) | undefined;

const visible = () => document.visibilityState === 'visible';
const pause = (ms: number) =>
  new Promise<void>((resolve) => {
    const t = window.setTimeout(done, ms);
    function done() {
      window.clearTimeout(t);
      wake = undefined;
      resolve();
    }
    wake = done;
  });

async function loop(): Promise<void> {
  if (running) return;
  running = true;
  try {
    while (started && visible()) {
      const known = currentSig;
      const t0 = Date.now();
      const c = (ctl = new AbortController());
      const next = await fetchUnread(fetch, { wait: WAIT_S, known, signal: c.signal });
      if (c.signal.aborted || !started || !visible()) break; // stopped, or hidden: read again on return
      const step = afterAnswer(next, known, Date.now() - t0, fails);
      fails = step.fails;
      if (step.show) {
        const prev = current;
        current = { count: next.count, latestTs: next.latestTs };
        currentSig = next.sig;
        listeners.forEach((fn) => fn(current, prev));
      }
      if (step.waitMs) await pause(step.waitMs);
    }
  } finally {
    running = false;
    ctl = undefined;
    if (started && visible()) void loop(); // stopped and started again while a request was out
  }
}
const onVisibility = () => {
  if (visible()) {
    wake?.(); // coming back: read now, not after a pause
    void loop();
  } else ctl?.abort();
};
function start(): void {
  if (started) return;
  started = true;
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('focus', onVisibility);
  void loop();
}
function stop(): void {
  started = false;
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('focus', onVisibility);
  ctl?.abort();
  wake?.();
}

/** Follow the shared count; returns the unsubscribe. The long-poll runs while anyone follows. */
export function subscribe(fn: (u: Unread, prev: Unread) => void): () => void {
  listeners.add(fn);
  start();
  return () => {
    listeners.delete(fn);
    if (!listeners.size) stop();
  };
}

/**
 * The unread count (null = unknown). `onArrived` fires when a new message comes in while the
 * EHR is open (not on the first read).
 */
export function useMessagesUnread(enabled: boolean, onArrived?: () => void): number | null {
  const [count, setCount] = useState<number | null>(enabled ? current.count : null);
  const cb = useRef(onArrived);
  cb.current = onArrived;
  useEffect(() => {
    if (!enabled) return;
    setCount(current.count);
    return subscribe((u, prev) => {
      setCount(u.count);
      if (arrived(prev, u)) cb.current?.();
    });
  }, [enabled]);
  return enabled ? count : null;
}
